import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';

import { Logo } from '../../components/Logo';

import { ApiError } from '../../api/http';
import { useAuthStore } from '../../stores/useAuthStore';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { status, login } = useAuthStore(
    useShallow((state) => ({ status: state.status, login: state.login })),
  );

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // 已經登入的人不該再看到登入頁；回到他原本想去的地方
  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setPending(true);

    try {
      await login(email, password);
      const from = (location.state as { from?: string } | null)?.from ?? '/';
      void navigate(from, { replace: true });
    } catch (caught) {
      // 後端刻意讓「帳號不存在」與「密碼錯」回同一句話，這裡照原樣顯示
      setError(caught instanceof ApiError ? caught.message : t('auth_error_unknown'));
    } finally {
      setPending(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-primary/5 p-6">
      <form
        className="w-full max-w-[420px] rounded-xl bg-card p-6 shadow-card ring-1 ring-foreground/10"
        onSubmit={(event) => void onSubmit(event)}
      >
        <Logo size={34} />
        <h1 className="mt-3 mb-1 text-2xl font-bold tracking-tight">{t('auth_login_title')}</h1>
        <p className="mb-4 text-sm text-muted-foreground">{t('auth_login_subtitle')}</p>

        <div className="flex flex-col gap-3">
          {error !== null && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Field>
            <FieldLabel htmlFor="login-email">{t('auth_email')}</FieldLabel>
            <Input
              id="login-email"
              type="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="login-password">{t('auth_password')}</FieldLabel>
            <Input
              id="login-password"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.currentTarget.value)}
            />
          </Field>

          <Button type="submit" disabled={pending}>
            {pending && <Spinner data-icon="inline-start" />}
            {t('auth_login_submit')}
          </Button>

          <Link
            to="/forgot-password"
            className="self-start text-sm text-muted-foreground hover:text-primary hover:underline"
          >
            {t('auth_forgot_password')}
          </Link>
        </div>

        <p className="mt-4 text-sm text-muted-foreground">
          {t('auth_no_account')}{' '}
          {/* 行內連結要有底線：跟旁邊的灰字只差在顏色的話，色弱的人分不出來（link-in-text-block） */}
          <Link to="/register" className="text-primary underline underline-offset-4">
            {t('auth_go_register')}
          </Link>
        </p>
      </form>
    </main>
  );
}
